import { formaterMontant } from '@admin-pmvb/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useState } from 'react'
import { LogoConnu, LogoDe } from '../composants/Logo'
import { requetes } from '../api'
import { Icone, type NomIcone } from '../composants/Icone'
import { EntetePage } from '../composants/Ui'
import {
  jourCourt,
  LIBELLES_ROLE_TIERS,
  LIBELLES_STATUT_ECHEANCE,
  LIBELLES_STATUT_PAIEMENT,
  LIBELLES_STATUT_PROJET,
  LIBELLES_STATUT_TACHE,
  LIBELLES_TYPE_PIECE,
} from '../libelles'

// Recherche dans toute l'appli : tâches, pièces, opérations du relevé, paiements, échéances et
// fournisseurs, chacun dans le périmètre de qui cherche. La saisie est gardée dans l'adresse.

/** Délai après la dernière frappe avant de chercher. */
const ATTENTE_MS = 300

const details = (...parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(' · ')

export function Recherche({
  recherche,
  onRechercher,
}: {
  recherche: string
  onRechercher: (texte: string) => void
}) {
  const [q, setQ] = useState(recherche)
  useEffect(() => {
    const t = q.trim()
    if (t === recherche) return
    const minuterie = setTimeout(() => onRechercher(t), ATTENTE_MS)
    return () => clearTimeout(minuterie)
  }, [q, recherche, onRechercher])
  const resultats = useQuery({
    ...requetes.recherche(recherche),
    enabled: recherche.length > 0,
    placeholderData: keepPreviousData,
  })
  const r = recherche ? resultats.data : undefined
  const rien =
    r &&
    r.taches.total +
      r.projets.total +
      r.pieces.total +
      r.transactions.total +
      r.paiements.total +
      r.echeances.total +
      r.tiers.total ===
      0

  return (
    <main>
      <EntetePage surtitre="Partout" titre="Recherche" />
      <div className="pile">
        <form
          className="recherche"
          onSubmit={(e) => {
            e.preventDefault()
            onRechercher(q.trim())
          }}
        >
          <label className="filtre">
            <Icone nom="search" />
            <input
              type="search"
              autoFocus
              aria-label="Rechercher partout"
              placeholder="Fournisseur, libellé, numéro, montant…"
              value={q}
              maxLength={100}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </form>
        {!recherche && (
          <p className="discret">
            Cherche dans les tâches, les projets (texte de leurs pages compris), les pièces (texte
            compris), les opérations du relevé, les paiements, les échéances et les fournisseurs.
            Chaque mot est exigé ; un montant (« 123,45 ») trouve aussi les opérations et paiements
            de ce montant.
          </p>
        )}
        {recherche && resultats.isPending && <p className="discret">Recherche…</p>}
        {resultats.isError && <p className="alerte">{resultats.error.message}</p>}
        {rien && <p className="discret">Rien trouvé pour « {recherche} ».</p>}
        {r && (
          <>
            <Categorie icone="task_alt" titre="Tâches" total={r.taches.total}>
              {r.taches.elements.map((t) => (
                <Link
                  key={t.id}
                  to="/taches/$id"
                  params={{ id: t.id }}
                  search={{ vue: 'toutes' }}
                  className="ligne ligne-lien"
                >
                  <div className="ligne-contenu">
                    <strong>{t.titre}</strong>
                    <div className="discret">
                      {details(
                        LIBELLES_STATUT_TACHE[t.statut],
                        t.entite,
                        t.dateDue && `pour le ${jourCourt(t.dateDue)}`,
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </Categorie>
            <Categorie icone="rocket_launch" titre="Projets" total={r.projets.total}>
              {r.projets.elements.map((p) => (
                <Link
                  key={p.id}
                  to="/projets/$id"
                  params={{ id: p.id }}
                  className="ligne ligne-lien"
                >
                  <div className="ligne-contenu">
                    <strong>{p.titre}</strong>
                    <div className="discret">
                      {details(LIBELLES_STATUT_PROJET[p.statut], p.entite ?? 'Sans entité')}
                    </div>
                  </div>
                </Link>
              ))}
            </Categorie>
            <Categorie
              icone="description"
              titre="Pièces"
              total={r.pieces.total}
              tout={
                <Link to="/pieces" search={{ vue: 'toutes', q: recherche }} className="lien-fleche">
                  Toutes <Icone nom="arrow_forward" />
                </Link>
              }
            >
              {r.pieces.elements.map((p) => (
                <Link
                  key={p.id}
                  to="/pieces/$id"
                  params={{ id: p.id }}
                  search={{ vue: 'toutes' }}
                  className="ligne ligne-lien"
                >
                  <LogoConnu nom={p.emetteur} />
                  <div className="ligne-contenu">
                    <strong>{p.emetteur ?? p.nomFichier ?? 'Pièce'}</strong>
                    <div className="discret">
                      {details(
                        LIBELLES_TYPE_PIECE[p.type],
                        p.numero,
                        p.entite ?? 'sans entité',
                        p.dateDocument && jourCourt(p.dateDocument),
                      )}
                    </div>
                    {p.extrait && <div className="discret extrait-piece">{p.extrait}</div>}
                  </div>
                  <span className="montant">
                    {p.montantCts !== null ? formaterMontant(p.montantCts, p.devise) : ''}
                  </span>
                </Link>
              ))}
            </Categorie>
            <Categorie
              icone="account_balance"
              titre="Opérations du relevé"
              total={r.transactions.total}
            >
              {r.transactions.elements.map((t) => (
                <Link
                  key={t.id}
                  to="/rapprochements/$id"
                  params={{ id: t.id }}
                  className="ligne ligne-lien"
                >
                  <LogoConnu nom={t.libelle} />
                  <div className="ligne-contenu">
                    <strong>{t.libelle}</strong>
                    <div className="discret">
                      {details(jourCourt(t.dateOperation), t.compte, t.entite)}
                    </div>
                  </div>
                  <span className="montant">{formaterMontant(t.montantCts, t.devise)}</span>
                </Link>
              ))}
            </Categorie>
            <Categorie icone="payments" titre="Paiements" total={r.paiements.total}>
              {r.paiements.elements.map((p) => {
                const contenu = (
                  <>
                    <LogoConnu nom={p.tiers ?? p.libelle} />
                    <div className="ligne-contenu">
                      <strong>{p.libelle ?? p.tiers ?? p.echeance ?? 'Paiement'}</strong>
                      <div className="discret">
                        {details(
                          p.sens === 'payer' ? 'À payer' : 'À encaisser',
                          LIBELLES_STATUT_PAIEMENT[p.statut],
                          p.entite,
                          p.datePrevue && jourCourt(p.datePrevue),
                        )}
                      </div>
                    </div>
                    <span className="montant">{formaterMontant(p.montantDuCts, p.devise)}</span>
                  </>
                )
                // Sa fiche : l'échéance qui le porte, sinon sa pièce, sinon la liste.
                return p.echeanceId ? (
                  <Link
                    key={p.id}
                    to="/echeances/$id"
                    params={{ id: p.echeanceId }}
                    className="ligne ligne-lien"
                  >
                    {contenu}
                  </Link>
                ) : p.pieceId ? (
                  <Link
                    key={p.id}
                    to="/pieces/$id"
                    params={{ id: p.pieceId }}
                    className="ligne ligne-lien"
                  >
                    {contenu}
                  </Link>
                ) : (
                  <Link
                    key={p.id}
                    to="/paiements"
                    search={{ vue: p.sens === 'payer' ? undefined : 'a_encaisser' }}
                    className="ligne ligne-lien"
                  >
                    {contenu}
                  </Link>
                )
              })}
            </Categorie>
            <Categorie icone="calendar_month" titre="Échéances" total={r.echeances.total}>
              {r.echeances.elements.map((e) => (
                <Link
                  key={e.id}
                  to="/echeances/$id"
                  params={{ id: e.id }}
                  className="ligne ligne-lien"
                >
                  <div className="ligne-contenu">
                    <strong>{e.libelle}</strong>
                    <div className="discret">
                      {details(
                        LIBELLES_STATUT_ECHEANCE[e.statut],
                        e.entite,
                        e.periode,
                        jourCourt(e.date),
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </Categorie>
            <Categorie icone="contacts" titre="Fournisseurs et tiers" total={r.tiers.total}>
              {r.tiers.elements.map((t) => (
                <Link key={t.id} to="/tiers/$id" params={{ id: t.id }} className="ligne ligne-lien">
                  <LogoDe nom={t.nom} />
                  <div className="ligne-contenu">
                    <strong>{t.nom}</strong>
                    <div className="discret">{LIBELLES_ROLE_TIERS[t.role]}</div>
                  </div>
                </Link>
              ))}
            </Categorie>
          </>
        )}
      </div>
    </main>
  )
}

/** Une catégorie trouvée : rien si elle est vide ; le total quand il dépasse ce qui est montré. */
function Categorie({
  icone,
  titre,
  total,
  tout,
  children,
}: {
  icone: NomIcone
  titre: string
  total: number
  tout?: ReactNode
  children: ReactNode[]
}) {
  if (total === 0) return null
  return (
    <section className="carte carte-flush">
      <div className="section-titre">
        <h2>
          <Icone nom={icone} />
          {titre}
          <span className="mono discret">
            {total > children.length ? `${children.length} sur ${total}` : total}
          </span>
        </h2>
        {total > children.length && tout}
      </div>
      {children}
    </section>
  )
}
